import type { ReactElement, ReactNode } from 'react';
import { Component } from '@/components/region/component';
import type { ComponentType } from '@/components/region';
import { TICKER_TIMING } from '@/extensions/ticker/lib/constants';
import { useTickerMarquee } from '@/extensions/ticker/hooks/use-ticker-marquee';
import { cn } from '@/lib/utils';

type TickerSlideProps = {
    component: ComponentType;
    isActive: boolean;
    isPaused: boolean;
    isSingleSlide: boolean;
    onCycleComplete?: () => void;
    onMarqueePause?: () => void;
    onMarqueeResume?: () => void;
    /** Storybook-only override for slide body content. */
    children?: ReactNode;
};

export function TickerSlide({
    component,
    isActive,
    isPaused,
    isSingleSlide,
    onCycleComplete,
    onMarqueePause,
    onMarqueeResume,
    children,
}: TickerSlideProps): ReactElement {
    const { viewportRef, contentRef, translateX, contentOverflows, shouldAnimate } = useTickerMarquee({
        isActive,
        isPaused,
        loop: isSingleSlide,
        startDelayMs: isSingleSlide ? 0 : TICKER_TIMING.slideTransitionMs,
        onCycleComplete: isSingleSlide ? undefined : onCycleComplete,
    });

    return (
        <div
            ref={viewportRef}
            className={cn('ticker-slide-viewport', contentOverflows && 'ticker-slide-viewport--scrollable')}
            aria-hidden={!isActive}
            onMouseEnter={() => {
                if (isActive) {
                    onMarqueePause?.();
                }
            }}
            onMouseLeave={() => {
                if (isActive) {
                    onMarqueeResume?.();
                }
            }}>
            <div
                ref={contentRef}
                className={cn(
                    'ticker-slide-content',
                    shouldAnimate && translateX !== 0 && 'ticker-slide-content--animating'
                )}
                style={shouldAnimate ? { transform: `translateX(${translateX}px)` } : undefined}>
                {children ?? <Component component={component} regionId="main" />}
            </div>
        </div>
    );
}
